import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { map } from "rxjs";

interface RiskLevelsComponentState {
    riskTableMax?: Partial<Record<ColorBox, number>>;
    populationDensityBoxes?: Partial<Record<RiskLevels, number>>;
    densityAfterMitigation: number | undefined;
}

export enum RiskLevels {
    Lowest = "LOWEST",
    Low = "LOW",
    Medium = "MEDIUM",
    High = "HIGH",
    Highest = "HIGHEST",
    Unknown = "UNKNOWN",
}
enum ColorBox {
    Yellow = "YELLOW",
    Violet = "VIOLET",
    Blue = "BLUE",
    Unknown = "UNKNOWN",
    Green = "GREEN",
    Red = "RED",
}

const MAX_VALUE_TO_DISPLAY = 100;

// eslint-disable-next-line no-magic-numbers
export const ROBUSTNESS_LEVELS_POPULATION_THRESHOLDS = [5, 50, 500, 5000, 50000];

export const densityLevels = [
    // eslint-disable-next-line no-magic-numbers
    [ROBUSTNESS_LEVELS_POPULATION_THRESHOLDS[3], RiskLevels.Highest],
    [ROBUSTNESS_LEVELS_POPULATION_THRESHOLDS[2], RiskLevels.High],
    [ROBUSTNESS_LEVELS_POPULATION_THRESHOLDS[1], RiskLevels.Medium],
    [ROBUSTNESS_LEVELS_POPULATION_THRESHOLDS[0], RiskLevels.Low],
    [0, RiskLevels.Lowest],
] as const;

@Component({
    selector: "dtm-ui-risk-levels-table",
    templateUrl: "./risk-levels-table.component.html",
    styleUrls: ["./risk-levels-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class RiskLevelsTableComponent {
    @Input() public set riskTableMax(value: Partial<Record<ColorBox, number>> | undefined) {
        this.localStore.patchState({ riskTableMax: value });
    }

    @Input() public set populationDensityBoxes(value: Partial<Record<RiskLevels, number>> | undefined) {
        this.localStore.patchState({ populationDensityBoxes: value });
    }

    @Input() public set densityAfterMitigation(value: number | undefined) {
        this.localStore.patchState({ densityAfterMitigation: value });
    }

    protected readonly riskTableMax$ = this.localStore.selectByKey("riskTableMax");
    protected readonly populationDensityBoxes$ = this.localStore.selectByKey("populationDensityBoxes");
    protected readonly densityAfterMitigation$ = this.localStore
        .selectByKey("densityAfterMitigation")
        .pipe(map((densityAfterMitigation) => this.mapDensityAfterMitigationToRiskLevel(densityAfterMitigation)));

    protected readonly ColorBox = ColorBox;
    protected readonly RiskLevels = RiskLevels;
    protected readonly MAX_VALUE_TO_DISPLAY = MAX_VALUE_TO_DISPLAY;

    constructor(private readonly localStore: LocalComponentStore<RiskLevelsComponentState>) {
        this.localStore.setState({
            riskTableMax: undefined,
            populationDensityBoxes: undefined,
            densityAfterMitigation: undefined,
        });
    }

    protected getBoxShareRatioColorBox(boxes: Partial<Record<ColorBox, number>> | undefined, key: ColorBox): number {
        if (!boxes) {
            return 0;
        }

        const sumOfBoxes = Object.values(boxes).reduce<number>((sum, value) => sum + (value ?? 0), 0);

        if (sumOfBoxes === 0) {
            return 0;
        }

        const currentBoxValue = boxes[key];

        return currentBoxValue ? currentBoxValue / sumOfBoxes : 0;
    }

    protected getBoxShareRatioRiskLevels(boxes: Partial<Record<RiskLevels, number>> | undefined, key: RiskLevels): number {
        if (!boxes) {
            return 0;
        }

        const sumOfBoxes = Object.values(boxes).reduce<number>((sum, value) => sum + (value ?? 0), 0);

        if (sumOfBoxes === 0) {
            return 0;
        }

        const currentBoxValue = boxes[key];

        return currentBoxValue ? currentBoxValue / sumOfBoxes : 0;
    }

    protected getRiskLevelGridValue(riskLevels: Partial<Record<RiskLevels, number>> | undefined, key: RiskLevels): string | undefined {
        const riskLevel = riskLevels?.[key];

        return (riskLevel ?? 0) > MAX_VALUE_TO_DISPLAY ? `>${MAX_VALUE_TO_DISPLAY}` : String(riskLevel ?? 0);
    }

    protected getColorBoxGridValue(colorBoxes: Partial<Record<ColorBox, number>> | undefined, key: ColorBox): string | undefined {
        const colorBox = colorBoxes?.[key];

        return (colorBox ?? 0) > MAX_VALUE_TO_DISPLAY ? `>${MAX_VALUE_TO_DISPLAY}` : String(colorBox ?? 0);
    }

    private mapDensityAfterMitigationToRiskLevel(densityAfterMitigation: number | null | undefined): RiskLevels {
        if (densityAfterMitigation == null) {
            return RiskLevels.Unknown;
        }

        return densityLevels.find(([threshold]) => densityAfterMitigation >= threshold)?.[1] ?? RiskLevels.Unknown;
    }
}
